// src/modules/operaciones/inventarios/hooks/useInforme.js
import { useState, useCallback, useRef } from 'react';
import { useApi } from '../../../../hooks/useApi';

// Un informe guardado debe traer la tabla original (columnas y filas) para que el dashboard pueda recalcular.
const esParseado = (x) => Boolean(x && typeof x === 'object' && Array.isArray(x.columnas) && Array.isArray(x.filas));

// Extrae el JSON parseado de la respuesta de GET /invent/informes/:id.
// `res.data` es el cuerpo del backend: { success, data: <parseado> }.
// También tolera que el parseado venga directo, dentro de `archivo` / `parseado` / `datos`, o como texto JSON.
function extraerParseado(cuerpo) {
  const candidatos = [];
  const agregar = (x) => {
    if (typeof x === 'string') {
      try {
        candidatos.push(JSON.parse(x));
      } catch (e) {
        /* no era JSON */
      }
    } else if (x && typeof x === 'object') {
      candidatos.push(x);
    }
  };

  agregar(cuerpo?.data);
  agregar(cuerpo);

  for (const base of [...candidatos]) {
    ['archivo', 'parseado', 'datos'].forEach((k) => agregar(base?.[k]));
  }

  // Lo ideal: un objeto con columnas y filas
  const completo = candidatos.find(esParseado);
  if (completo) return completo;

  // Informe antiguo sin tabla (solo insights / gráficos): el viewer sabe mostrarlo
  return (
    candidatos.find((c) => c && (c.insights || c.graficoBarras || c.graficoTorta)) || null
  );
}

export default function useInforme() {
  const { request } = useApi();
  const [estado, setEstado] = useState("idle");
  const [error, setError] = useState(null);
  const [parseado, setParseado] = useState(null);
  const [archivoOriginal, setArchivoOriginal] = useState(null);
  const [guardados, setGuardados] = useState([]);
  const [cargandoInforme, setCargandoInforme] = useState(false);

  // Identifica la última carga pedida: si llega una respuesta vieja, se descarta
  const ultimaCarga = useRef(0);

  const cargarGuardados = useCallback(async () => {
    try {
      const res = await request('/invent/informes', 'GET');
      if (res && res.success) {
        const arrayReal = Array.isArray(res.data) 
          ? res.data 
          : (res.data?.data && Array.isArray(res.data.data) ? res.data.data : []);
        setGuardados(arrayReal);
      }
    } catch (err) { 
      console.error("Error al cargar informes:", err); 
    }
  }, [request]);

  const subirArchivo = useCallback(async (archivo) => {
    if (!archivo) return;
    setArchivoOriginal(archivo);
    setEstado("uploading");
    setError(null);
    const formData = new FormData();
    formData.append("archivo", archivo);

    try {
      setEstado("parsing");
      const token = localStorage.getItem("token");
      const baseUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${baseUrl}/api/invent/informes/procesar`, {
        method: 'POST',
        headers: { ...(token && { Authorization: `Bearer ${token}` }) },
        body: formData
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || data.message || "Error al procesar el archivo");
      
      setParseado(data);
      setEstado("ready");
    } catch (err) {
      setError(err.message);
      setEstado("error");
    }
  }, []);

  // Abre un informe guardado: GET /invent/informes/:id → parseado completo.
  // Devuelve true si se cargó, false si falló (el error ya lo muestra useApi con Swal).
  const cargarInforme = useCallback(async (id) => {
    if (id === undefined || id === null || id === '') return false;

    const miCarga = ++ultimaCarga.current;
    setCargandoInforme(true);
    setError(null);

    try {
      const res = await request(`/invent/informes/${id}`, 'GET');
      if (miCarga !== ultimaCarga.current) return false; // llegó otra petición más nueva

      if (!res || !res.success) {
        setError("No se pudo cargar el informe");
        return false;
      }

      const datos = extraerParseado(res.data);
      if (!datos) {
        setError("El informe guardado no tiene datos legibles");
        return false;
      }

      setArchivoOriginal(null); // no hay archivo local: viene de la base de datos
      setParseado(datos);
      setEstado("ready");
      return true;
    } catch (err) {
      if (miCarga === ultimaCarga.current) {
        console.error("Error al cargar el informe:", err);
        setError(err.message || "No se pudo cargar el informe");
      }
      return false;
    } finally {
      if (miCarga === ultimaCarga.current) setCargandoInforme(false);
    }
  }, [request]);

  const guardarInforme = useCallback(async (datosInforme) => {
    const res = await request('/invent/informes/guardar', 'POST', { ...datosInforme, archivo: parseado });
    if (!res.success) throw new Error("No se pudo guardar");
    await cargarGuardados();
  }, [request, parseado, cargarGuardados]);

  const eliminarInforme = useCallback(async (id) => {
    const res = await request(`/invent/informes/${id}`, 'DELETE');
    if (!res.success) throw new Error("No se pudo eliminar");
    await cargarGuardados();
  }, [request, cargarGuardados]);

  const reset = useCallback(() => {
    setEstado("idle"); setError(null); setParseado(null); setArchivoOriginal(null);
  }, []);

  return {
    estado, error, parseado, archivoOriginal, guardados, cargandoInforme,
    subirArchivo, guardarInforme, cargarGuardados, cargarInforme, eliminarInforme, reset
  };
}